/**
 * Mobile-only experiment: same /products API as main grid, minimal UI.
 * Each card shows the first image; carousel starts only after touch on the image (not click).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useContextData } from '../../ApiContext';
import { MOB_PRODUCTS_PAGE_SIZE } from '../../Slider/SliderMobNew';
import { SimpleTouchCarouselCard } from './SimpleTouchCarouselCard';
import { ProductGridSkeleton } from '../../Components/images/Mob/ProductGridSkeleton';
import { ensureMediaUrl } from '../../utils/mediaUrl';
import './ProductsMobTouchExperiment.scss';

export function ProductsMobTouchExperiment() {
    const { queryParams, setQueryParams } = useContextData();
    const [queryParamsInit, setQueryParamsInit] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setQueryParams({
            category: [],
            subcategory: [],
            price: [0, 10000],
        });
        setQueryParamsInit(true);
    }, [setQueryParams]);

    useEffect(() => {
        if (!queryParamsInit) return;

        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem('token');
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const response = await axios.post(
                    '/products',
                    { ...queryParams, page: 1, size: MOB_PRODUCTS_PAGE_SIZE },
                    config
                );
                const data = response.data || [];
                if (cancelled) return;
                const mapped = data
                    .filter((item) => item.urls && item.urls.length > 0)
                    .map((item) => ({
                        id: item._id,
                        urls: item.urls.map((u) => ensureMediaUrl(u)),
                        title: item.title,
                        price: item.price,
                    }));
                setItems(mapped);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.response?.data?.message || e?.message || 'Failed to load');
                    setItems([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [queryParams, queryParamsInit]);

    return (
        <div className="mob-touch-exp">
            <header className="mob-touch-exp__header">
                <Link to="/" className="mob-touch-exp__back">
                    ← Home
                </Link>
                <h1 className="mob-touch-exp__title">Touch carousel experiment</h1>
                <p className="mob-touch-exp__sub">
                    Same API as the shop grid. Touch the <strong>image</strong> to start auto-advance (not
                    click). One <code>&lt;img&gt;</code> swap per slide.
                </p>
            </header>

            {loading ? (
                <ProductGridSkeleton cells={MOB_PRODUCTS_PAGE_SIZE} />
            ) : error ? (
                <p className="mob-touch-exp__error" role="alert">
                    {error}
                </p>
            ) : (
                <div className="mob-touch-exp__grid">
                    {items.map((product) => (
                        <SimpleTouchCarouselCard key={product.id} product={product} />
                    ))}
                </div>
            )}
        </div>
    );
}
